import React, { useState, useEffect, useCallback } from 'react';
import { FileSpreadsheet, ArrowRight, Cpu, Package, Network, CheckSquare, Layers, ClipboardCheck, Compass } from 'lucide-react';
import { useDbNavigate } from '../../context/DatabaseContext';
import { reportingApi } from '../../services/api';

/**
 * ReportsPage — landing/dashboard for the Reports module.
 *
 * Live-derive pattern (Josh 2026-08-06 refactor from hardcoded groups):
 *   - Fetches /api/report/configs → server-filters by caller's reports.*
 *     permissions (same as Sidebar nav). Returns { slug, name, description,
 *     section } for reports the caller can see.
 *   - Groups client-side by `section`, renders under section headers that
 *     mirror the nav-tree hierarchy (General, Submittals, Networking,
 *     Quality, Commissioning, Design, Dynamic).
 *   - Hardcoded reports (only OAT Commissioning Keys today) are injected
 *     into their section alongside the dynamic ones — same pattern as
 *     Sidebar.js.
 *   - Sections with zero visible reports are hidden entirely.
 *
 * Adding a new report = SQL insert to AppCfg_ModuleDynamic (Config_Type
 * = 'report') with a valid `section` field. No code deploy required.
 */

// Section metadata — order matches the Reports nav tree in navigationConfig.js
const SECTION_META = [
  { id: 'general',       label: 'General',       icon: Cpu },
  { id: 'submittals',    label: 'Submittals',    icon: Package },
  { id: 'networking',    label: 'Networking',    icon: Network },
  { id: 'quality',       label: 'Quality',       icon: CheckSquare },
  { id: 'commissioning', label: 'Commissioning', icon: ClipboardCheck },
  { id: 'design',        label: 'Design',        icon: Compass },
  { id: 'dynamic',       label: 'Dynamic',       icon: Layers },
];

// Hardcoded reports (non-DB-backed pages). Keyed by section so we can
// inject them alongside the dynamic ones. Match the Sidebar hardcoded
// children in navigationConfig.js.
const HARDCODED_BY_SECTION = {
  quality: [
    {
      slug: 'oat-commissioning-keys',
      name: 'OAT Commissioning Keys',
      description: 'OAT (Owner Acceptance Testing) commissioning key sheets — printable PDF checklists for each tag, with prerequisites, general requirements, and close-out steps. Hardcoded React page (not in AppCfg_ModuleDynamic).'
    }
  ]
};

const ReportsPage = () => {
  const navigate = useDbNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportingApi.listConfigs();
      setReports(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group dynamic reports by section, then merge in hardcoded children.
  // Section order comes from SECTION_META so we mirror the nav tree.
  const groups = SECTION_META.map(section => {
    const dynamic = reports.filter(r => (r.section || '').toLowerCase() === section.id);
    const hardcoded = HARDCODED_BY_SECTION[section.id] || [];
    return { ...section, reports: [...hardcoded, ...dynamic] };
  }).filter(g => g.reports.length > 0);

  const totalReports = groups.reduce((sum, g) => sum + g.reports.length, 0);

  if (loading) {
    return (
      <div className="stui-dashboard-body">
        <div className="stui-dashboard-header">
          <FileSpreadsheet size={20} className="stui-dashboard-header-icon" />
          <h1 className="stui-dashboard-header-title">Reports</h1>
        </div>
        <div className="stui-module-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="stui-dashboard-body">
      <div className="stui-dashboard-header">
        <FileSpreadsheet size={20} className="stui-dashboard-header-icon" />
        <h1 className="stui-dashboard-header-title">Reports</h1>
        <span className="stui-dashboard-header-subtitle">
          {totalReports} {totalReports === 1 ? 'report' : 'reports'} available
        </span>
      </div>

      <div className="stui-dashboard-groups">
        {groups.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.id}>
              <div className="stui-dashboard-group-header">
                <GroupIcon size={16} className="stui-dashboard-header-icon" />
                <span className="stui-dashboard-group-title">{group.label}</span>
                <span className="stui-dashboard-group-count">
                  {group.reports.length} {group.reports.length === 1 ? 'report' : 'reports'}
                </span>
              </div>
              <div className="stui-dashboard-tile-grid">
                {group.reports.map(report => (
                  <div
                    key={report.slug}
                    className="stui-tile stui-clickable"
                    onClick={() => navigate(`/reports/${report.slug}`)}
                  >
                    <div className="stui-tile-header">
                      <span className="stui-tile-title">{report.name || report.slug}</span>
                      <ArrowRight size={16} className="stui-dashboard-tile-action" />
                    </div>
                    {report.description && (
                      <div className="stui-dashboard-tile-content">
                        <p className="stui-dashboard-tile-description">{report.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsPage;
